"use client";

import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Menu, Plus, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
const seoStrategyServices = [{
  label: "National SEO",
  href: "/national-seo"
}, {
  label: "International SEO",
  href: "/international-seo"
}, {
  label: "Local SEO",
  href: "/local-seo"
}, {
  label: "Technical SEO",
  href: "/technical-seo"
}, {
  label: "SEO Audits",
  href: "/seo-audits"
}, {
  label: "AI Search Optimisation",
  href: "/ai-search-optimisation"
}];
const specialistSeoServices = [{
  label: "Ecommerce SEO",
  href: "/ecommerce-seo"
}, {
  label: "B2B SEO & Lead Generation",
  href: "/b2b-seo-lead-generation"
}];
function DesktopMenuConsumer({
  navMode
}: {
  navMode: "dark" | "light";
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const shouldReduceMotion = useReducedMotion();
  const textColorClass = navMode === "dark" ? "text-[var(--background)]" : "text-[var(--foreground)]";
  if (loading) {
    return <div className="flex items-center gap-8">
        {[0, 1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-3 w-16 bg-[var(--muted)]/20 animate-pulse rounded-md" />)}
      </div>;
  }
  return <ul className="flex items-center gap-8 lg:gap-10">
      {menuItems.map((item, i) => {
      const isServices = item.label.toLowerCase().includes("service");
      const hasChildren = Boolean(item.children && item.children.length > 0);
      const hasDropdown = isServices || hasChildren;
      return <li key={item.id} data-index={i} className="relative" onMouseEnter={() => hasDropdown && setOpenDropdown(item.id)} onMouseLeave={() => hasDropdown && setOpenDropdown(null)}>
            {hasDropdown ? <Button variant="ghost" size="sm" onClick={() => setOpenDropdown(openDropdown === item.id ? null : item.id)} className={`flex items-center gap-1.5 h-auto p-0 text-[0.9rem] font-semibold uppercase tracking-[0.16em] transition-colors duration-300 hover:opacity-70 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--primary)] focus-visible:ring-offset-[3px] focus-visible:ring-offset-transparent ${textColorClass}`} aria-expanded={openDropdown === item.id}>
                <span>{item.label}</span>
                <Plus className="size-3 transition-transform duration-300" style={{
            transform: openDropdown === item.id ? "rotate(45deg)" : "rotate(0)"
          }} />
              </Button> : <Link to={item.href || "#"} className={`text-[0.9rem] font-semibold uppercase tracking-[0.16em] transition-colors duration-300 hover:opacity-70 ${textColorClass}`}>
                {item.label}
              </Link>}

            {hasDropdown && <AnimatePresence>
                {openDropdown === item.id && <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: -8,
            height: 0
          }} animate={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 1,
            y: 0,
            height: "auto"
          }} exit={shouldReduceMotion ? {
            opacity: 0
          } : {
            opacity: 0,
            y: -8,
            height: 0
          }} transition={{
            type: "spring",
            stiffness: 220,
            damping: 28
          }} className={isServices ? "absolute left-1/2 top-full mt-4 -translate-x-1/2 w-[780px] max-w-[calc(100vw-3rem)] overflow-hidden z-50" : "absolute left-0 top-full mt-4 min-w-[220px] overflow-hidden z-50"}>
                    {isServices ? <div className="bg-[var(--background)] rounded-lg shadow-xl p-10 border border-[var(--border)] grid grid-cols-3 gap-10 text-left">
                        <div>
                          <h3 className="text-[0.85rem] font-bold uppercase tracking-wider text-[var(--primary)] mb-4">
                            SEO STRATEGY
                          </h3>
                          <ul className="flex flex-col gap-2.5">
                            {seoStrategyServices.map((child, ci) => <motion.li key={child.href} data-index={ci} initial={shouldReduceMotion ? {
                    opacity: 1
                  } : {
                    opacity: 0,
                    y: 6
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    delay: shouldReduceMotion ? 0 : 0.03 * ci + 0.03,
                    duration: 0.25
                  }}>
                                <Link to={child.href} className="font-sans text-base font-medium text-[var(--foreground)] hover:text-[var(--accent)] hover:underline transition-colors duration-200 block py-0.5" onClick={() => setOpenDropdown(null)}>
                                  {child.label}
                                </Link>
                              </motion.li>)}
                          </ul>
                        </div>

                        <div>
                          <h3 className="text-[0.85rem] font-bold uppercase tracking-wider text-[var(--primary)] mb-4">
                            SPECIALIST SEO
                          </h3>
                          <ul className="flex flex-col gap-2.5">
                            {specialistSeoServices.map((child, ci) => <motion.li key={child.href} data-index={ci} initial={shouldReduceMotion ? {
                    opacity: 1
                  } : {
                    opacity: 0,
                    y: 6
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    delay: shouldReduceMotion ? 0 : 0.03 * ci + 0.1,
                    duration: 0.25
                  }}>
                                <Link to={child.href} className="font-sans text-base font-medium text-[var(--foreground)] hover:text-[var(--accent)] hover:underline transition-colors duration-200 block py-0.5" onClick={() => setOpenDropdown(null)}>
                                  {child.label}
                                </Link>
                              </motion.li>)}
                          </ul>
                        </div>

                        <div className="border-l border-[var(--border)] pl-10 flex flex-col justify-between">
                          <div>
                            <h3 className="text-[0.85rem] font-bold uppercase tracking-wider text-[var(--primary)] mb-3">
                              WORK DIRECTLY WITH ME
                            </h3>
                            <p className="text-xs text-[var(--muted-foreground)] leading-relaxed mb-6 font-sans">
                              Get personalised SEO strategy and consultancy directly from Yassine Essaadi — without unnecessary agency layers.
                            </p>
                          </div>
                          <Link to="/ContactPage" onClick={() => setOpenDropdown(null)}>
                            <Button className="w-full rounded-md px-5 py-2.5 text-[0.85rem] font-semibold uppercase tracking-wider bg-[var(--primary)] text-[var(--primary-foreground)] hover:bg-[var(--accent)] transition-all duration-300 shadow">
                              Hire Me Directly →
                            </Button>
                          </Link>
                        </div>
                      </div> : <div className="bg-[var(--background)] rounded-lg shadow-xl p-6 border border-[var(--border)] text-left">
                        <ul className="flex flex-col gap-2.5">
                          {item.children?.map((child, ci) => <motion.li key={child.id || child.href || ci} data-index={ci} initial={shouldReduceMotion ? {
                  opacity: 1
                } : {
                  opacity: 0,
                  y: 6
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: shouldReduceMotion ? 0 : 0.03 * ci + 0.03,
                  duration: 0.25
                }}>
                              <Link to={child.href || "#"} className="font-sans text-base font-medium text-[var(--foreground)] hover:text-[var(--accent)] hover:underline transition-colors duration-200 block py-0.5 whitespace-nowrap" onClick={() => setOpenDropdown(null)}>
                                {child.label}
                              </Link>
                            </motion.li>)}
                        </ul>
                      </div>}
                  </motion.div>}
              </AnimatePresence>}
          </li>;
    })}
    </ul>;
}
function MobileMenuConsumer({
  onNavigate
}: {
  onNavigate: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});
  const toggleItem = (id: string) => {
    setOpenItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  if (loading) {
    return <div className="flex flex-col gap-6">
        {[0, 1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-10 w-40 bg-[var(--muted)]/20 animate-pulse rounded-md" />)}
      </div>;
  }
  return <ul className="flex flex-col divide-y divide-[var(--border)]">
      {menuItems.map((item, i) => {
      const isServices = item.label.toLowerCase().includes("service");
      const hasChildren = Boolean(item.children && item.children.length > 0);
      const hasDropdown = isServices || hasChildren;
      const isOpen = Boolean(openItems[item.id]);
      return <li key={item.id} data-index={i} className="py-5">
            {hasDropdown ? <div>
                <Button variant="ghost" size="sm" onClick={() => toggleItem(item.id)} className="flex w-full items-center justify-between h-auto p-0 font-serif text-[2rem] text-[var(--foreground)] hover:opacity-70">
                  <span>{item.label}</span>
                  <Plus className="size-6 transition-transform duration-300" style={{
              transform: isOpen ? "rotate(45deg)" : "rotate(0)"
            }} />
                </Button>
                <AnimatePresence>
                  {isOpen && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: "auto"
            }} exit={{
              opacity: 0,
              height: 0
            }} transition={{
              duration: 0.32,
              ease: "easeOut"
            }} className="overflow-hidden">
                      {isServices ? <div className="pt-4 pl-2 flex flex-col gap-5">
                          <div>
                            <h4 className="text-sm font-bold uppercase tracking-wider text-[var(--primary)] mb-2.5">
                              SEO Strategy
                            </h4>
                            <ul className="flex flex-col gap-2 pl-2">
                              {seoStrategyServices.map((child, ci) => <li key={child.href} data-index={ci}>
                                  <Link to={child.href} onClick={onNavigate} className="block font-sans text-lg text-[var(--foreground)] opacity-85 hover:opacity-100 hover:text-[var(--primary)] transition-all py-0.5">
                                    {child.label}
                                  </Link>
                                </li>)}
                            </ul>
                          </div>

                          <div>
                            <h4 className="text-sm font-bold uppercase tracking-wider text-[var(--primary)] mb-2.5">
                              Specialist SEO
                            </h4>
                            <ul className="flex flex-col gap-2 pl-2">
                              {specialistSeoServices.map((child, ci) => <li key={child.href} data-index={ci}>
                                  <Link to={child.href} onClick={onNavigate} className="block font-sans text-lg text-[var(--foreground)] opacity-85 hover:opacity-100 hover:text-[var(--primary)] transition-all py-0.5">
                                    {child.label}
                                  </Link>
                                </li>)}
                            </ul>
                          </div>
                        </div> : <div className="pt-4 pl-2 flex flex-col gap-2">
                          <ul className="flex flex-col gap-2 pl-2">
                            {item.children?.map((child, ci) => <li key={child.id || child.href || ci} data-index={ci}>
                                <Link to={child.href || "#"} onClick={onNavigate} className="block font-sans text-lg text-[var(--foreground)] opacity-85 hover:opacity-100 hover:text-[var(--primary)] transition-all py-0.5">
                                  {child.label}
                                </Link>
                              </li>)}
                          </ul>
                        </div>}
                    </motion.div>}
                </AnimatePresence>
              </div> : <Link to={item.href || "#"} onClick={onNavigate} className="block font-serif text-[2rem] text-[var(--foreground)] hover:opacity-70 transition-opacity">
                {item.label}
              </Link>}
          </li>;
    })}
    </ul>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [navMode, setNavMode] = useState<"dark" | "light">("light");
  const [mobileOpen, setMobileOpen] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 80);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const computeActive = () => {
      const sections = Array.from(document.querySelectorAll<HTMLElement>("[data-nav]"));
      if (sections.length === 0) return;
      const probeY = 64;
      let active: HTMLElement | null = null;
      for (const sec of sections) {
        const rect = sec.getBoundingClientRect();
        if (rect.top <= probeY && rect.bottom > probeY) {
          active = sec;
          break;
        }
      }
      if (!active) {
        let closest: HTMLElement | null = null;
        let closestDist = Infinity;
        for (const sec of sections) {
          const rect = sec.getBoundingClientRect();
          const dist = Math.abs(rect.top - probeY);
          if (dist < closestDist) {
            closestDist = dist;
            closest = sec;
          }
        }
        active = closest;
      }
      if (active) {
        const mode = active.getAttribute("data-nav");
        if (mode === "dark" || mode === "light") {
          setNavMode(mode);
        }
      }
    };
    computeActive();
    const observer = new IntersectionObserver(() => computeActive(), {
      rootMargin: "-60px 0px -80% 0px",
      threshold: [0, 0.01, 0.99, 1]
    });
    observerRef.current = observer;
    const sections = document.querySelectorAll("[data-nav]");
    sections.forEach(s => observer.observe(s));
    window.addEventListener("scroll", computeActive, {
      passive: true
    });
    window.addEventListener("resize", computeActive);
    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", computeActive);
      window.removeEventListener("resize", computeActive);
    };
  }, []);
  const textColorClass = navMode === "dark" ? "text-[var(--background)]" : "text-[var(--foreground)]";
  return <section data-section-id="23125" id="main-navigation" data-nav="light" className="fixed inset-x-0 top-0 z-50 w-full bg-background text-foreground" style={{
    paddingTop: "clamp(0.75rem, 1.6vw, 1.5rem)"
  }}>
      <div className={`mx-auto max-w-[1440px] px-[clamp(1.25rem,4vw,3rem)] transition-all duration-300`}>
        <div className={`flex items-center justify-between py-4 px-6 rounded-lg transition-all duration-300 ${scrolled ? "backdrop-blur-md bg-[var(--background)]/60" : "bg-transparent"}`} style={{
        backdropFilter: scrolled ? "blur(12px)" : "none"
      }}>
          <div className="flex-none">
            <Link to="/" className={`font-serif italic text-[32px] leading-none transition-colors duration-300 hover:opacity-70 ${textColorClass}`} aria-label="YE Consultancy home">
              YE
            </Link>
          </div>

          <nav className="hidden lg:flex flex-1 items-center justify-center">
            <MenuProvider menu_id="46" className="header-menu">
              <DesktopMenuConsumer navMode={navMode} />
            </MenuProvider>
          </nav>

          <div className="flex-none flex items-center gap-3">
            <Link to="/ContactPage" className="hidden lg:inline-flex">
              <Button className={`rounded-[6px] px-8 h-auto text-[0.9rem] font-semibold uppercase tracking-[0.16em] transition-all duration-300 shadow ${navMode === "dark" ? "bg-[var(--background)] text-[var(--foreground)] hover:bg-[var(--accent)]" : "bg-[var(--primary)] text-[var(--primary-foreground)] hover:bg-[var(--accent)]"}`}>
                Hire Me Directly
              </Button>
            </Link>

            <div className="lg:hidden">
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label="Open menu" className={`${textColorClass}`}>
                    <Menu className="size-6" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-full sm:max-w-md bg-[var(--background)] border-l border-[var(--border)] p-0">
                  <SheetTitle className="sr-only">Navigation menu</SheetTitle>
                  <div className="flex h-full flex-col">
                    <div className="flex items-center justify-between px-6 py-5 border-b border-[var(--border)]">
                      <span className="font-serif italic text-[32px] leading-none text-[var(--foreground)]">
                        YE
                      </span>
                      <Button variant="ghost" size="icon" onClick={() => setMobileOpen(false)} aria-label="Close menu" className="text-[var(--foreground)]">
                        <X className="size-6" />
                      </Button>
                    </div>
                    <div className="flex-1 overflow-y-auto px-6 py-8">
                      <MenuProvider menu_id="46" className="header-menu-mobile">
                        <MobileMenuConsumer onNavigate={() => setMobileOpen(false)} />
                      </MenuProvider>
                    </div>
                    <div className="px-6 py-6 border-t border-[var(--border)]">
                      <Link to="/ContactPage" onClick={() => setMobileOpen(false)}>
                        <Button className="w-full rounded-md px-8 h-auto text-[0.9rem] font-semibold uppercase tracking-[0.16em] bg-[var(--primary)] text-[var(--primary-foreground)] hover:bg-[var(--accent)] shadow">
                          Hire Me Directly
                        </Button>
                      </Link>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </div>
    </section>;
}